import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { getAvailableSlots } from "../apis/bookingApi";
import ViewDialog from "./ViewDialog";
import { format } from "date-fns/format";

// Define interfaces for our types
interface BookedSlot {
  startTime: string;
  endTime: string;
}

interface DateAvailability {
  date: string;
  bookedSlots: BookedSlot[];
  loading: boolean;
  error: string | null;
  hasOverlap: boolean;
}

interface AvailableSlotsProps {
  selectedDate?: string;
  selectedDates?: string[];
  startTime: string;
  endTime: string;
  listingId: string | undefined;
  dateSelectionType?: "single" | "multiple" | "range";
  individualDateTimes?: { [key: string]: { startTime: string; endTime: string } };
}

const AvailableSlots: React.FC<AvailableSlotsProps> = ({
  selectedDate,
  selectedDates = [],
  startTime,
  endTime,
  listingId,
  dateSelectionType = "single",
  individualDateTimes = {},
}) => {
  const [dateAvailabilities, setDateAvailabilities] = useState<DateAvailability[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showCelebrationDialog, setShowCelebrationDialog] = useState(false);
  


  // Extract time from date string (e.g., "Fri Jun 13 2025 17:00:00 GMT-0300" to "17:00")
  const extractTimeFromDateString = (dateString: string): string => {
    const date = new Date(dateString);
    return `${String(date.getHours()).padStart(2, "0")}:${String(
      date.getMinutes()
    ).padStart(2, "0")}`;
  };

  // Format time for display (convert "17:00" to "5:00 PM")
  const formatTimeDisplay = (timeStr: string): string => {
    // Check if it's a full date string
    if (timeStr.includes("GMT")) {
      timeStr = extractTimeFromDateString(timeStr);
    }

    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  // Convert time string "HH:MM" to minutes
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  // Check if user selected time overlaps with booked slots
  const checkOverlap = useCallback((bookedSlots: BookedSlot[], checkStartTime: string, checkEndTime: string): boolean => {
    if (!checkStartTime || !checkEndTime || bookedSlots.length === 0) return false;

    return bookedSlots.some((slot) => {
      // Convert all times to minutes for easier comparison
      const selectedStart = timeToMinutes(checkStartTime);
      const selectedEnd = timeToMinutes(checkEndTime);
      const slotStart = timeToMinutes(slot.startTime);
      const slotEnd = timeToMinutes(slot.endTime);

      // Check for overlap
      return (
        (selectedStart >= slotStart && selectedStart < slotEnd) ||
        (selectedEnd > slotStart && selectedEnd <= slotEnd) ||
        (selectedStart <= slotStart && selectedEnd >= slotEnd)
      );
    });
  }, []);

  // Grouped time slots for better visualization
  const getGroupedTimeSlots = useCallback((bookedSlots: BookedSlot[]): BookedSlot[] => {
    if (bookedSlots.length === 0) return [];

    const merged: BookedSlot[] = [];
    let current: BookedSlot | null = null;

    // Sort slots by start time
    const sorted = [...bookedSlots].sort(
      (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
    );

    for (const slot of sorted) {
      if (!current) {
        current = { ...slot };
      } else if (
        timeToMinutes(current.endTime) >= timeToMinutes(slot.startTime)
      ) {
        // Merge overlapping slots
        if (timeToMinutes(current.endTime) < timeToMinutes(slot.endTime)) {
          current.endTime = slot.endTime;
        }
      } else {
        merged.push(current);
        current = { ...slot };
      }
    }

    if (current) {
      merged.push(current);
    }

    return merged;
  }, []);

  // Fetch booked slots for a single date
  const fetchBookedSlotsForDate = useCallback(async (date: string): Promise<DateAvailability> => {
    try {
      const response = await getAvailableSlots(date, listingId!);
      
      if (response.data.message === "Free Booking") {
        setShowCelebrationDialog(true);
      }

      // Format the booked slots data
      let formattedSlots: BookedSlot[] = [];
      if (response && response.data.bookedSlots) {
        formattedSlots = response.data.bookedSlots.map((slot: any) => ({
          startTime: extractTimeFromDateString(slot.startTime),
          endTime: extractTimeFromDateString(slot.endTime),
        }));
      }

      // Determine which times to check for overlap
      let checkStartTime = startTime;
      let checkEndTime = endTime;

      // For individual times, get the times for this specific date
      if (dateSelectionType === "multiple" && individualDateTimes) {
        // Use the date string directly instead of converting to avoid timezone issues
        const dateKey = date;
        const dateTimes = individualDateTimes[dateKey];
        
        if (dateTimes && dateTimes.startTime && dateTimes.endTime) {
          checkStartTime = dateTimes.startTime;
          checkEndTime = dateTimes.endTime;
        }
        // If no times selected yet, still show booked slots but no overlap check
      }

      // Only check overlap if we have both start and end times
      const hasOverlap = checkStartTime && checkEndTime ? checkOverlap(formattedSlots, checkStartTime, checkEndTime) : false;

      return {
        date,
        bookedSlots: formattedSlots,
        loading: false,
        error: null,
        hasOverlap,
      };
    } catch (err) {
      console.error("Error fetching booked slots for date:", date, err);
      return {
        date,
        bookedSlots: [],
        loading: false,
        error: "Unable to load booked slots. Please try again.",
        hasOverlap: false,
      };
    }
  }, [listingId, dateSelectionType, checkOverlap]);

  // Memoize the dates to check to prevent unnecessary re-computations
  const datesToCheck = useMemo(() => {
    let dates: string[] = [];

    // Determine which dates to check based on selection type
    switch (dateSelectionType) {
      case "single":
        if (selectedDate) {
          dates = [selectedDate];
        }
        break;
      case "multiple":
        dates = selectedDates;
        break;
      case "range":
        // For range, use all selectedDates (they should contain the full range)
        dates = selectedDates;
        break;
      default:
        if (selectedDate) {
          dates = [selectedDate];
        }
    }

    return dates;
  }, [selectedDate, selectedDates, dateSelectionType]);



  // Fetch booked slots from API for all relevant dates
  useEffect(() => {
    let isMounted = true;
    
    if (!listingId || datesToCheck.length === 0) {
      setDateAvailabilities([]);
      return;
    }

    const fetchAllBookedSlots = async () => {
      if (!isMounted) return;
      
      setLoading(true);
      setError(null);

      try {
        // Initialize loading states for all dates
        const initialAvailabilities: DateAvailability[] = datesToCheck.map(date => ({
          date,
          bookedSlots: [],
          loading: true,
          error: null,
          hasOverlap: false,
        }));

        if (isMounted) {
          setDateAvailabilities(initialAvailabilities);
        }

        // Fetch availability for all dates concurrently
        const availabilityPromises = datesToCheck.map(date => fetchBookedSlotsForDate(date));
        const availabilities = await Promise.all(availabilityPromises);
        if (isMounted) {
          setDateAvailabilities(availabilities);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching booked slots:", err);
          setError("Unable to load booked slots. Please try again.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAllBookedSlots();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [listingId, datesToCheck.join(',')]);

  // Check if any date has overlap
  const hasAnyOverlap = dateAvailabilities.some(availability => availability.hasOverlap);

  // Get total booked slots across all dates
  const totalBookedSlots = dateAvailabilities.reduce((total, availability) => {
    return total + availability.bookedSlots.length;
  }, 0);
  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <EventBusyIcon sx={{ color: "error.main", mr: 1, fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight="medium">
            Booked Slots
          </Typography>
          {dateAvailabilities.length > 1 && (
            <Chip
              label={`${dateAvailabilities.length} dates checked`}
              size="small"
              color="info"
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
              minHeight: 100,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          </Paper>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : datesToCheck.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
              minHeight: 100,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", py: 2 }}>
              <EventAvailableIcon sx={{ color: "info.main", mr: 1 }} />
              <Typography color="info.main">
                {dateSelectionType === "single" 
                  ? "Please select a date to see availability" 
                  : dateSelectionType === "multiple"
                  ? "Please select one or more dates to see availability"
                  : "Please select a date range to see availability"
                }
              </Typography>
            </Box>
          </Paper>
        ) : dateAvailabilities.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
              minHeight: 100,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", py: 2 }}>
              <EventAvailableIcon sx={{ color: "success.main", mr: 1 }} />
              <Typography color="success.main">
                {dateSelectionType === "multiple" && individualDateTimes && Object.keys(individualDateTimes).length > 0
                  ? "All slots available on these dates. Select times to check for conflicts."
                  : "All slots available on these dates"
                }
              </Typography>
            </Box>
          </Paper>
        ) : (
          <>
            {hasAnyOverlap && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Your selected time overlaps with already booked slots on one or more dates.
              </Alert>
            )}

            {dateAvailabilities.length === 1 ? (
              // Single date - show directly with enhanced date header
              <Paper
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  overflow: "hidden",
                }}
              >
                {/* Date Header */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CalendarTodayIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="subtitle2" fontWeight="bold">
                      {(() => {
                        // Parse the date string directly to avoid timezone issues
                        const [year, month, day] = dateAvailabilities[0].date.split('-').map(Number);
                        const displayDate = new Date(year, month - 1, day);
                        return format(displayDate, "EEEE, MMMM d, yyyy");
                      })()}
                    </Typography>
                  </Box>
                  {dateAvailabilities[0].hasOverlap ? (
                    <Chip
                      label="Overlap Detected"
                      color="error"
                      size="small"
                      icon={<EventBusyIcon />}
                    />
                                     ) : dateAvailabilities[0].bookedSlots.length === 0 ? (
                     <Chip
                       label={dateSelectionType === "multiple" && individualDateTimes && Object.keys(individualDateTimes).length > 0
                         ? "Available - Set Times"
                         : "All Available"
                       }
                       color="success"
                       size="small"
                       icon={<EventAvailableIcon />}
                     />
                  ) : (
                    <Chip
                      label={`${dateAvailabilities[0].bookedSlots.length} booked slot${dateAvailabilities[0].bookedSlots.length !== 1 ? 's' : ''}`}
                      color="warning"
                      size="small"
                      icon={<EventBusyIcon />}
                    />
                  )}
                </Box>

                {/* Content */}
                <Box sx={{ p: 2 }}>
                  {dateAvailabilities[0].error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {dateAvailabilities[0].error}
                    </Alert>
                                     ) : dateAvailabilities[0].bookedSlots.length === 0 ? (
                     <Box sx={{ display: "flex", alignItems: "center", py: 2 }}>
                       <EventAvailableIcon sx={{ color: "success.main", mr: 1 }} />
                       <Typography color="success.main">
                         {dateSelectionType === "multiple" && individualDateTimes && Object.keys(individualDateTimes).length > 0
                           ? "All slots available on this date. Select times to check for conflicts."
                           : "All slots available on this date"
                         }
                       </Typography>
                     </Box>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                        The following time slots are already booked:
                      </Typography>

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {getGroupedTimeSlots(dateAvailabilities[0].bookedSlots).map((slot, index) => (
                          <Chip
                            key={index}
                            label={`${formatTimeDisplay(slot.startTime)} - ${formatTimeDisplay(slot.endTime)}`}
                            color="error"
                            variant="outlined"
                            icon={<EventBusyIcon />}
                            sx={{
                              borderColor: "error.main",
                              "& .MuiChip-icon": { color: "error.main" },
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              </Paper>
            ) : (
              // Multiple dates - show in accordion with enhanced headers
              <Paper
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                {/* Summary Header */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CalendarTodayIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="subtitle2" fontWeight="bold">
                      Availability Summary
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Chip
                      label={`${dateAvailabilities.length} dates`}
                      size="small"
                      color="info"
                    />
                    {totalBookedSlots > 0 && (
                      <Chip
                        label={`${totalBookedSlots} total booked slots`}
                        size="small"
                        color="warning"
                      />
                    )}
                  </Box>
                </Box>

                {dateAvailabilities.map((availability, index) => (
                  <Box key={index}>
                    <Accordion 
                      defaultExpanded={availability.hasOverlap || availability.bookedSlots.length > 0}
                      sx={{
                        '&:before': { display: 'none' },
                        boxShadow: 'none',
                        borderBottom: index < dateAvailabilities.length - 1 ? 1 : 0,
                        borderColor: 'divider',
                      }}
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          bgcolor: availability.hasOverlap ? 'error.lighter' : 
                                  availability.bookedSlots.length > 0 ? 'warning.lighter' : 'success.lighter',
                          '&:hover': {
                            bgcolor: availability.hasOverlap ? 'error.light' : 
                                    availability.bookedSlots.length > 0 ? 'warning.light' : 'success.light',
                          }
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                            <CalendarTodayIcon sx={{ mr: 1, color: "primary.main", fontSize: 18 }} />
                            <Typography variant="subtitle2" fontWeight="bold">
                              {(() => {
                                // Parse the date string directly to avoid timezone issues
                                const [year, month, day] = availability.date.split('-').map(Number);
                                const displayDate = new Date(year, month - 1, day);
                                return format(displayDate, "EEEE, MMMM d");
                              })()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {availability.hasOverlap && (
                              <Chip
                                label="Overlap"
                                color="error"
                                size="small"
                                icon={<EventBusyIcon />}
                              />
                            )}
                            {availability.bookedSlots.length > 0 && !availability.hasOverlap && (
                              <Chip
                                label={`${availability.bookedSlots.length} booked`}
                                color="warning"
                                size="small"
                                icon={<EventBusyIcon />}
                              />
                            )}
                                                         {availability.bookedSlots.length === 0 && !availability.error && !availability.hasOverlap && (
                               <Chip
                                 label={dateSelectionType === "multiple" && individualDateTimes && Object.keys(individualDateTimes).length > 0
                                   ? "Available - Set Times"
                                   : "Available"
                                 }
                                 color="success"
                                 size="small"
                                 icon={<EventAvailableIcon />}
                               />
                             )}
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ bgcolor: 'background.paper' }}>
                        {availability.error ? (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {availability.error}
                          </Alert>
                                                 ) : availability.bookedSlots.length === 0 ? (
                           <Box sx={{ display: "flex", alignItems: "center", py: 1 }}>
                             <EventAvailableIcon sx={{ color: "success.main", mr: 1 }} />
                             <Typography color="success.main">
                               {dateSelectionType === "multiple" && individualDateTimes && Object.keys(individualDateTimes).length > 0
                                 ? "All slots available on this date. Select times to check for conflicts."
                                 : "All slots available on this date"
                               }
                             </Typography>
                           </Box>
                        ) : (
                          <>
                            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                              The following time slots are already booked:
                            </Typography>

                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                              {getGroupedTimeSlots(availability.bookedSlots).map((slot, slotIndex) => (
                                <Chip
                                  key={slotIndex}
                                  label={`${formatTimeDisplay(slot.startTime)} - ${formatTimeDisplay(slot.endTime)}`}
                                  color="error"
                                  variant="outlined"
                                  icon={<EventBusyIcon />}
                                  sx={{
                                    borderColor: "error.main",
                                    "& .MuiChip-icon": { color: "error.main" },
                                  }}
                                />
                              ))}
                            </Box>
                          </>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                ))}
              </Paper>
            )}
          </>
        )}
      </Box>
      <ViewDialog
        open={showCelebrationDialog}
        onClose={() => setShowCelebrationDialog(false)}
        title={"🎉 We're Celebrating — And You're Invited! 🎉"}
        sections={[]}
        specialContent={{
          title: "Company Anniversary - August 10",
          content: (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ fontSize: 60, mb: 1, color: 'warning.main', lineHeight: 1 }}>
                🎊
              </Box>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Dear Valued Customer,
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                August 10 is a <b>very special day</b> for us — it's our <span style={{ color: '#ff9800', fontWeight: 600 }}>company anniversary</span>!<br/>
                This milestone wouldn't be possible without your continued support, and we're truly grateful to have you as part of our journey.
              </Typography>
              <Paper elevation={3} sx={{ bgcolor: 'success.lighter', p: 2, mb: 2, border: '2px dashed #43a047' }}>
                <Typography variant="body1" fontWeight="bold" color="success.dark">
                  To mark the occasion, <span style={{ color: '#43a047' }}>all bookings are paused for the day</span>,<br/>
                  but you're invited to enjoy a <span style={{ color: '#43a047' }}>1-hour session completely free</span> — our gift to you!
                </Typography>
              </Paper>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Whether you've been with us from the beginning or just recently joined, this celebration is as much yours as it is ours.<br/>
                <b>Come by, say hello, and let us thank you in person!</b>
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                With heartfelt thanks,<br/>
                <span style={{ fontWeight: 600 }}>The Bat Cage Team</span>
              </Typography>
            </Box>
          )
        }}
      />
    </>
  );
};

export default AvailableSlots;
